import React, { useContext, useEffect, useRef, useState } from "react";
import { Joyride, ACTIONS, EVENTS, STATUS } from "react-joyride";
import { useNavigate } from "react-router-dom";
import AuthContext from "../Auth/AuthContext";
import { fetchController } from "../../utils/FetchController/fetchController";
import { getHostDomain } from "../../utils/utils";
import { TOURS } from "./tourRegistry";
import { useTour } from "./TourContext";

const SKIP_FLAG = "tutorial_skipped_session";

// Poll until `selector` matches a node or `timeoutMs` elapses. Resolves true
// when found; resolves false on timeout. Used to wait for a page to mount
// after a `navigateTo` between tour steps.
function waitForSelector(selector, timeoutMs = 5000, intervalMs = 60) {
  if (!selector || selector === "body") return Promise.resolve(true);
  return new Promise((resolve) => {
    const start = Date.now();
    const tick = () => {
      if (document.querySelector(selector)) return resolve(true);
      if (Date.now() - start >= timeoutMs) return resolve(false);
      setTimeout(tick, intervalMs);
    };
    tick();
  });
}

async function persistTutorialComplete(authUser, setAuthUser) {
  if (!authUser?.id) return;
  const baseUrl = getHostDomain();
  try {
    await fetchController(
      `${baseUrl}/api/v1/user/${authUser.id}`,
      "PUT",
      { tutorial_completed_at: new Date().toISOString() }
    );
    setAuthUser((u) =>
      u ? { ...u, tutorial_completed_at: new Date().toISOString() } : u
    );
  } catch (e) {
    // Soft-fail: the tour is over either way; persistence is best-effort.
    // The next backend fetch will reveal whether it stuck.
    // eslint-disable-next-line no-console
    console.warn("Failed to persist tutorial completion", e);
  }
}

export default function TourEngine() {
  const { currentTour, endTour, runId } = useTour();
  const { authUser, setAuthUser } = useContext(AuthContext);
  const navigate = useNavigate();

  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  // Hold the steps locally so a mid-tour registry change doesn't reset us.
  const [steps, setSteps] = useState([]);
  const persistOnExit = useRef(false);
  // Guards handleEvent against re-entry. Joyride can fire several events in
  // quick succession during navigation/rerender; without this guard each
  // STEP_AFTER would increment stepIndex and we'd skip past every other step.
  const isAdvancing = useRef(false);

  useEffect(() => {
    if (currentTour && TOURS[currentTour]) {
      const t = TOURS[currentTour];
      // Forgive registry entries that still use the v2 disableBeacon name.
      setSteps(
        t.steps.map((s) => ({
          ...s,
          skipBeacon: s.skipBeacon ?? s.disableBeacon ?? false,
        }))
      );
      persistOnExit.current = t.onComplete === "persist";
      setStepIndex(0);
      setRun(true);
    } else {
      setRun(false);
      setSteps([]);
    }
  }, [currentTour, runId]);

  // react-joyride v3 renamed `callback` -> `onEvent`. Two args now:
  // (data, controls). We manage stepIndex ourselves (controlled mode).
  async function handleEvent(data /* , controls */) {
    const { status, type, index, action } = data;
    // eslint-disable-next-line no-console
    console.log("[tour] event", { type, action, index, status });

    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      if (persistOnExit.current) {
        await persistTutorialComplete(authUser, setAuthUser);
      }
      if (status === STATUS.SKIPPED && persistOnExit.current) {
        try {
          sessionStorage.setItem(SKIP_FLAG, "1");
        } catch (_) {}
      }
      endTour();
      return;
    }

    // Only advance/retreat on STEP_AFTER (the user clicked Next, Back, or
    // Done). TARGET_NOT_FOUND must NOT be coerced into an advance — it
    // fires during route transitions and would otherwise cascade past
    // every step whose target isn't immediately in the DOM.
    if (type !== EVENTS.STEP_AFTER) return;

    // Re-entry guard. Joyride can fire STEP_AFTER more than once when run/
    // stepIndex change during our async advance work; if we re-enter, each
    // call increments stepIndex and the tour skips steps.
    if (isAdvancing.current) {
      // eslint-disable-next-line no-console
      console.log("[tour] re-entry blocked");
      return;
    }
    isAdvancing.current = true;

    try {
      // ACTIONS.PREV = Back; everything else here (NEXT / CLOSE on a
      // close-as-next button) means forward.
      const dir = action === ACTIONS.PREV ? -1 : 1;
      const nextIndex = index + dir;
      const nextStep = steps[nextIndex];

      // Off the end of the tour ⇒ user clicked Done on the last step.
      if (!nextStep) {
        if (persistOnExit.current) {
          await persistTutorialComplete(authUser, setAuthUser);
        }
        endTour();
        return;
      }

      if (nextStep.navigateTo) {
        // Route change. Don't toggle `run` — leave Joyride mounted and just
        // update stepIndex once the destination is settled. Toggling run
        // during navigation was triggering extra STEP_AFTER events that
        // skipped steps via re-entry.
        navigate(nextStep.navigateTo);
        // Wait for a specific marker if the step needs one. Body targets
        // and centered modals don't need to wait — Joyride will float them
        // wherever <body> is, even mid-route-transition.
        const sel = nextStep.waitFor || (nextStep.target !== "body" ? nextStep.target : null);
        if (sel) {
          await waitForSelector(sel);
        } else {
          // Give React Router a beat to swap the route before we point
          // Joyride at the next step.
          await new Promise((r) => setTimeout(r, 120));
        }
        setStepIndex(nextIndex);
      } else {
        setStepIndex(nextIndex);
      }
    } finally {
      isAdvancing.current = false;
    }
  }

  if (!steps.length) return null;

  return (
    <Joyride
      key={runId}
      steps={steps}
      run={run}
      stepIndex={stepIndex}
      onEvent={handleEvent}
      continuous
      scrollToFirstStep
      locale={{
        back: "Back",
        close: "Exit",
        last: "Done",
        next: "Next",
        skip: "Exit tour",
      }}
      // v3: top-level `options` replaces the v2 `styles.options` bag.
      options={{
        showProgress: true,
        // Don't dismiss when the user clicks the dark backdrop — the
        // overlay surrounds spotlit targets and accidental clicks are easy.
        overlayClickAction: false,
        // Explicit button list. `'primary'` is the Next/Done button;
        // `'close'` is the X in the corner; `'skip'` is the labeled exit.
        buttons: ["back", "primary", "skip", "close"],
        // X corner button behaves like Skip — closes the whole tour
        // instead of just advancing to the next step.
        closeButtonAction: "skip",
        // Esc also exits the tour cleanly.
        dismissKeyAction: "skip",
        primaryColor: "#3182CE",
        textColor: "#1A202C",
        backgroundColor: "#FFFFFF",
        overlayColor: "rgba(0, 0, 0, 0.45)",
        arrowColor: "#FFFFFF",
        zIndex: 10000,
      }}
      styles={{
        tooltipContainer: { textAlign: "left" },
        // v3 renamed buttonNext -> buttonPrimary.
        buttonPrimary: { fontWeight: 600 },
        buttonBack: { color: "#3182CE" },
        buttonSkip: { color: "#718096" },
      }}
    />
  );
}

export { SKIP_FLAG };
